import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';

/**
 * Os concorrentes da empresa (etapa 12): os sócios informam, o conselheiro da
 * empresa vê. É o que evita que um mesmo conselheiro atenda concorrentes: o
 * vínculo de um conselheiro com conflito é recusado no HAOps.
 */

type Concorrente = { id: string; nome: string; cnpj: string | null; origem: 'SOCIO' | 'HAOPS'; informado_por: string | null };
type Resposta = { pode_editar: boolean; concorrentes: Concorrente[] };

const campo = 'rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm';
const botao = 'rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50';

function cnpjFormatado(c: string | null) {
  return c && c.length === 14 ? `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}` : c;
}

export function Concorrentes({ empresaId }: { empresaId: string }) {
  const qc = useQueryClient();
  const chave = ['conselheiro-concorrentes', empresaId];
  const q = useQuery({
    queryKey: chave,
    queryFn: async () => (await api.get<Resposta>(`/conselheiro/empresas/${empresaId}/concorrentes`)).data,
  });
  const [nome, setNome] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const aoMudar = {
    onSuccess: (d: Resposta) => {
      setErro(null);
      qc.setQueryData(chave, d);
    },
    onError: (e: unknown) => setErro(extractError(e).message),
  };
  const incluir = useMutation({
    mutationFn: async () =>
      (await api.post<Resposta>(`/conselheiro/empresas/${empresaId}/concorrentes`, { nome: nome.trim(), cnpj: cnpj.trim() || null })).data,
    ...aoMudar,
    onSuccess: (d: Resposta) => {
      aoMudar.onSuccess(d);
      setNome('');
      setCnpj('');
    },
  });
  const tirar = useMutation({
    mutationFn: async (id: string) => (await api.delete<Resposta>(`/conselheiro/concorrentes/${id}`)).data,
    ...aoMudar,
  });

  if (q.isLoading) return <p className="text-sm text-ha-subtle">Carregando…</p>;
  if (q.isError || !q.data) return null;
  const { pode_editar, concorrentes } = q.data;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-ha-muted">
        {pode_editar
          ? 'Quem disputa os mesmos clientes que vocês. Serve para que nenhum conselheiro de vocês atenda um concorrente ao mesmo tempo. Com o CNPJ, a checagem é mais certeira.'
          : 'Informados pelos sócios. Um conselheiro que atende um concorrente não pode ser vinculado a esta empresa.'}
      </p>
      {concorrentes.length === 0 ? (
        <p className="text-sm text-ha-subtle">Nenhum concorrente informado.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {concorrentes.map((c) => (
            <li key={c.id} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span>
                <span className="font-medium text-ha-text">{c.nome}</span>
                {c.cnpj ? <span className="ml-2 text-xs text-ha-muted">{cnpjFormatado(c.cnpj)}</span> : null}
                {c.origem === 'HAOPS' ? <span className="ml-2 text-xs text-ha-muted">(equipe Hack)</span> : null}
              </span>
              {pode_editar && c.origem === 'SOCIO' ? (
                <button
                  type="button"
                  className="text-xs text-ha-muted underline hover:text-ha-text"
                  disabled={tirar.isPending}
                  onClick={() => tirar.mutate(c.id)}
                >
                  tirar
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {pode_editar ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (nome.trim().length >= 2) incluir.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-xs text-ha-muted">
            Concorrente
            <input className={`${campo} w-56`} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome da empresa" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ha-muted">
            CNPJ (opcional)
            <input className={`${campo} w-44`} value={cnpj} onChange={(e) => setCnpj(e.target.value)} placeholder="00.000.000/0000-00" />
          </label>
          <button type="submit" className={botao} style={{ background: 'var(--primary)' }} disabled={incluir.isPending || nome.trim().length < 2}>
            Incluir
          </button>
        </form>
      ) : null}
      {erro ? <p className="text-sm text-red-600">{erro}</p> : null}
    </div>
  );
}
